import { useIntl } from 'react-intl'
import {
	Button,
	DataTable,
	TableBody,
	TableColumn,
	TableHeader,
	TableRow,
} from 'react-md'
import LoadingState from 'ui/components/LoadingState'
import type { Action } from 'ui/components/ViiluFullPageDialog'
import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import type { CompanyMassLinkItem } from 'state/hausvise-actions'
import {
	_getHausviseCompaniesByManagerUUID,
	_hausviseCompaniesMassLink,
} from 'state/hausvise-actions'
import { useManagerCompanies } from 'ui/ManagerPropertyManagersCertificate/hooks'
import { useSelectedManager } from 'util/hooks/useSelectedManager'
import { useCallback, useEffect, useState } from 'react'
import { error } from 'state/notifyActions'
import { useDispatch } from 'react-redux'

// TODO: If the hausvise code ever is migrated to TS
//       this could be moved into some shared place
type HausviseCompany = {
	hausviseCompanyId: string
	name: string
	businessId: string
	address: string
	zip: string
	area: string
	country: string
}

const useHausviseCompanies = (managerUUID: string) => {
	const intl = useIntl()
	const [loading, setLoading] = useState(true)
	const [companies, setCompanies] = useState<HausviseCompany[] | null>(null)

	const dispatch = useDispatch()

	const showErrorNotification = useCallback(
		(notifMsg: string) => {
			dispatch(error(`❌ ${notifMsg}`))
		},
		[dispatch],
	)

	useEffect(() => {
		let mounted = true
		const doFetch = async () => {
			setLoading(true)

			const addApartments = false
			const res: any = await _getHausviseCompaniesByManagerUUID(
				managerUUID,
				addApartments,
			)

			if (!mounted) return

			if (res?.data) {
				const companies: HausviseCompany[] = res.data
				setCompanies(companies)
			} else {
				// if we didn't get data, let's log the res so it's
				// easier to debug in the future
				console.log('_getHausviseCompaniesByManagerUUID res', res)

				const missingApiKeyMessage = intl.formatMessage({
					defaultMessage:
						'Isännöitsijän asetuksista puuttuu Hausvise-valtuutusavain.',
					description:
						'Error message for a missing Hausvise integration API key.',
				})
				const missingHvIntegrationMsg = intl.formatMessage({
					defaultMessage:
						'Isännöitsijän asetuksista puuttuu Hausvise-integraatio.',
					description: 'Error message for a missing Hausvise integration.',
				})
				const companyListNotFoundMessage = intl.formatMessage({
					defaultMessage: 'Yhtiölistaa ei löytynyt isännöitsijän tiedoilla.',
					description:
						'Message notifying the user that the company list search failed.',
				})
				if (res?.success === false) {
					const errorCode = res.ex?.details?.code
					if (errorCode === 'missing-api-key') {
						showErrorNotification(missingApiKeyMessage)
					} else if (errorCode === 'no-hausvise-integration') {
						showErrorNotification(missingHvIntegrationMsg)
					} else {
						showErrorNotification(companyListNotFoundMessage)
					}
				} else {
					showErrorNotification(companyListNotFoundMessage)
				}
			}

			setLoading(false)
		}
		doFetch()
		return () => {
			mounted = false
		}
	}, [intl, managerUUID, showErrorNotification])

	return { loading, companies }
}

type HausviseMassLinkDialogProps = {
	onHide: () => void
}

const HausviseMassLinkDialog = (props: HausviseMassLinkDialogProps) => {
	const { onHide } = props
	const intl = useIntl()

	const { loading: loadingSelectedManager, selectedManager } =
		useSelectedManager()
	const managerUUID = selectedManager ? selectedManager.uuid : 'no-value'
	const {
		loading: loadingManagerCompanies,
		companies: viiluCompaniesFromQuery,
	} = useManagerCompanies(managerUUID)
	const { loading: loadingHausviseCompanies, companies: hausviseCompanies } =
		useHausviseCompanies(managerUUID)
	const viiluCompanies = viiluCompaniesFromQuery?.filter(
		(vc) => !vc.integrations?.hausvise?.[managerUUID],
	)
	let loading =
		loadingSelectedManager ||
		loadingManagerCompanies ||
		loadingHausviseCompanies

	const [selectedUUIDs, setSelectedUUIDs] = useState<string[]>([])
	const [massLinkingStage, setMassLinkingStage] = useState<
		'processing' | 'done' | 'error' | null
	>(null)
	const [massLinkingUi, setMassLinkingUi] = useState<JSX.Element | null>(null)

	loading ||= massLinkingStage === 'processing'

	const doTheMassLinking = async () => {
		setMassLinkingStage('processing')

		const companiesToLink: CompanyMassLinkItem[] = [
			...new Set(
				selectedUUIDs
					.map((uuid) => {
						const vc = viiluCompanies.find((c) => c.uuid === uuid)
						const hc = hausviseCompanies?.find(
							(c) => c.businessId === vc?.businessId,
						)
						if (vc && hc) {
							const item: CompanyMassLinkItem = {
								viiluUUID: vc.uuid,
								hausviseId: hc.hausviseCompanyId,
							}
							return item
						} else {
							return null
						}
					})
					.filter((l): l is CompanyMassLinkItem => !!l),
			),
		]

		const res = await _hausviseCompaniesMassLink({
			companiesToLink,
		})
		if (!res?.success) {
			console.error(res.ex)
			setMassLinkingUi(
				<>
					{intl.formatMessage({
						defaultMessage: '❌ Massalinkitys ei onnistunut',
						description: 'Mass linking failed',
					})}
				</>,
			)
			setMassLinkingStage('error')
			return
		}

		setMassLinkingStage('done')
		setMassLinkingUi(
			<>
				{intl.formatMessage(
					{
						defaultMessage: '✅ Massalinkitys onnistui {count} kohteelle',
						description: 'Mass linking succeeded for {count} companies',
					},
					{
						count: companiesToLink.length,
					},
				)}
			</>,
		)
	}

	const renderContent = () => {
		if (viiluCompanies.length === 0) {
			return (
				<div className="flex-center full-width full-height">
					{intl.formatMessage({
						defaultMessage: 'Ei linkitettäviä kohteita',
						description: 'No companies to link',
					})}
				</div>
			)
		}
		return (
			<DataTable
				onRowToggle={(rowId, checked) => {
					if (rowId === 0) {
						if (checked) {
							setSelectedUUIDs(() => viiluCompanies.map((c) => c.uuid))
						} else {
							setSelectedUUIDs([])
						}
					} else {
						if (checked) {
							setSelectedUUIDs((prevUUIDs) => {
								const uuid = viiluCompanies[rowId - 1]?.uuid
								if (typeof uuid === 'string' && !prevUUIDs.includes(uuid)) {
									return [...prevUUIDs, uuid]
								}
								return prevUUIDs
							})
						} else {
							setSelectedUUIDs((prevUUIDs) => {
								const uuid = viiluCompanies[rowId - 1].uuid
								return prevUUIDs.filter((u) => u !== uuid)
							})
						}
					}
				}}
			>
				<TableHeader>
					<TableRow>
						<TableColumn>Viilu Nimi</TableColumn>
						<TableColumn>Hausvise Nimi</TableColumn>
						<TableColumn>Y-Tunnus</TableColumn>
					</TableRow>
				</TableHeader>
				<TableBody>
					{viiluCompanies.map((viiluCompany) => {
						const hausviseCompany = hausviseCompanies?.find(
							(v) => v.businessId === viiluCompany.businessId,
						)
						return (
							<TableRow key={viiluCompany.uuid}>
								<TableColumn>{viiluCompany.name}</TableColumn>
								<TableColumn>
									{hausviseCompany?.name ??
										intl.formatMessage({
											defaultMessage: 'Ei vastaavaa kohdetta hausvisessä',
											description: 'No matching hausvise company',
										})}
								</TableColumn>
								<TableColumn>{viiluCompany.businessId}</TableColumn>
							</TableRow>
						)
					})}
				</TableBody>
			</DataTable>
		)
	}

	let actions: Action[] | undefined
	if (!massLinkingStage) {
		actions = [
			{
				id: 'link-selected-companies-button',
				onClick: () => doTheMassLinking(),
				children: intl.formatMessage({
					defaultMessage: 'Linkitä valitut kohteet',
					description: 'Link selected companies',
				}),
			},
		]
	} else if (massLinkingStage === 'done') {
		actions = [
			{
				id: 'close-button',
				onClick: () => onHide(),
				children: intl.formatMessage({
					defaultMessage: 'Sulje',
					description: 'Close',
				}),
			},
		]
	} else {
		actions = undefined
	}

	let pageContent: JSX.Element
	if (massLinkingStage === 'done') {
		pageContent = (
			<div className="full-width full-height flex-center">{massLinkingUi}</div>
		)
	} else if (loading) {
		pageContent = (
			<div className="full-width full-height flex-center">
				<LoadingState />
			</div>
		)
	} else {
		pageContent = renderContent()
	}

	return (
		<ViiluFullPageDialog
			id="hausvise-mass-link-dialog"
			title={intl.formatMessage({
				defaultMessage: 'Massalinkitys',
				description: 'Mass linking',
			})}
			visible
			onHide={onHide}
			focusOnMount
			toolbarStyle={{
				background: 'var(--color-secondary-dark)',
			}}
			nav={
				<Button icon onClick={onHide}>
					close
				</Button>
			}
			actions={actions}
		>
			{pageContent}
		</ViiluFullPageDialog>
	)
}

export default HausviseMassLinkDialog
